import { ChevronDown, Lock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  WeightGlyph,
  badgeStyles,
  cn,
} from '@/components/ui';
import { WEIGHT_OPTIONS, getWeightStatus } from '@/logic/weights';

/**
 * A pair's weight as a badge (shape + label, never a status color) that opens
 * a menu of the four weights.
 */
export const WeightMenu = ({
  weight,
  pairLabel,
  onSetWeight,
  className,
}: {
  weight: number;
  pairLabel: string;
  onSetWeight: (weight: number) => void;
  className?: string;
}) => {
  const status = getWeightStatus(weight);
  const current =
    WEIGHT_OPTIONS.find((option) => option.status === status) ??
    WEIGHT_OPTIONS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Weight for pair ${pairLabel}: ${current.label}`}
        className={badgeStyles({
          tone: status === 'fixed' ? 'accent' : 'neutral',
          size: 'md',
          interactive: true,
          className: cn(
            'gap-1.5 pl-2 pr-1.5 data-[state=open]:ring-1 data-[state=open]:ring-border-strong',
            status === 'ignore' && 'bg-transparent',
            className
          ),
        })}
      >
        <WeightGlyph
          status={status}
          size={10}
          className={status === 'ignore' ? 'text-fg-subtle' : 'text-current'}
        />
        <span>{current.label}</span>
        {status === 'fixed' && <Lock aria-hidden="true" />}
        <ChevronDown aria-hidden="true" className="opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72">
        <DropdownMenuLabel>Weight · pair {pairLabel}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={status}
          onValueChange={(next) => {
            const option = WEIGHT_OPTIONS.find((item) => item.status === next);
            if (option && option.status !== status) onSetWeight(option.value);
          }}
        >
          {WEIGHT_OPTIONS.map((option) => (
            <DropdownMenuRadioItem
              key={option.status}
              value={option.status}
              icon={<WeightGlyph status={option.status} />}
              className="items-start [&>span:nth-child(2)]:mt-0.5"
            >
              <span className="flex min-w-0 flex-col">
                <span>{option.label}</span>
                <span className="text-xs leading-snug text-fg-muted">
                  {option.description}
                </span>
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
